import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useJumboLayout } from "@jumbo/hooks";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Divider,
  Modal,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Switch,
  FormControlLabel,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import {
  format,
  differenceInMinutes,
  differenceInHours,
  differenceInSeconds,
} from "date-fns";
import { sk } from "date-fns/locale";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import TakeoutDiningIcon from "@mui/icons-material/TakeoutDining";
import styled, { keyframes } from "styled-components";
import {
  DISPLAY_SETTINGS,
  GRID_CONFIG,
  ORDER_BOX_COLORS,
  STYLED_COMPONENTS,
  MODAL_CONFIG,
} from "./orderSettings";
import {
  PREPARATION_STATUSES,
  preparationStatusColor,
  preparationStatusLabelKey,
} from "../preparationStatusMeta";
import {
  getDSOKOrdersForStatusScreen,
  updateOrderPreparationStatus,
  updateOrderPreparationStatusBatch,
  getBEUrl,
  setBackendDomain,
} from "app/services/api/management";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useSocket } from "app/providers/SocketProvider";
import jwtAuthKioskAxios from "app/services/auth/jwtAuthKiosk";
import { getProjectEnvBasedOnCustomerBackendDomain } from "app/utils/appHelpers";

// Animation keyframes
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
`;

const moveToReady = keyframes`
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  25% {
    transform: translateY(-20px) scale(1.1);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-40px) scale(1.05);
    opacity: 0.6;
  }
  75% {
    transform: translateY(-60px) scale(1.02);
    opacity: 0.4;
  }
  100% {
    transform: translateY(-80px) scale(1);
    opacity: 0;
  }
`;

const moveToInProgress = keyframes`
  0% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
  25% {
    transform: translateY(20px) scale(1.1);
    opacity: 0.8;
  }
  50% {
    transform: translateY(40px) scale(1.05);
    opacity: 0.6;
  }
  75% {
    transform: translateY(60px) scale(1.02);
    opacity: 0.4;
  }
  100% {
    transform: translateY(80px) scale(1);
    opacity: 0;
  }
`;

const moveFromReady = keyframes`
  0% {
    transform: translateY(80px) scale(1);
    opacity: 0;
  }
  25% {
    transform: translateY(60px) scale(1.02);
    opacity: 0.4;
  }
  50% {
    transform: translateY(40px) scale(1.05);
    opacity: 0.6;
  }
  75% {
    transform: translateY(20px) scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

const moveFromInProgress = keyframes`
  0% {
    transform: translateY(-80px) scale(1);
    opacity: 0;
  }
  25% {
    transform: translateY(-60px) scale(1.02);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-40px) scale(1.05);
    opacity: 0.6;
  }
  75% {
    transform: translateY(-20px) scale(1.1);
    opacity: 0.8;
  }
  100% {
    transform: translateY(0) scale(1);
    opacity: 1;
  }
`;

// Styled components for the sections
const SectionContainer = styled.div`
  margin-bottom: 10px;
  padding: 2rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const InProgressSection = styled(SectionContainer)`
  background-color: rgba(76, 175, 80, 0.15);
  border: 2px solid rgba(76, 175, 80, 0.3);
  margin-top: 10px;
  position: relative;
`;

const ReadySection = styled(SectionContainer)`
  background-color: rgba(33, 150, 243, 0.15);
  border: 2px solid rgba(33, 150, 243, 0.3);
  position: relative;
`;

const BackgroundInProgressTitle = styled(Typography)`
  position: absolute !important;
  top: 1px !important;
  left: 30px !important;
  font-weight: 700 !important;
  font-size: 2rem !important;
  color: rgba(76, 175, 80, 0.6) !important;
  z-index: 1 !important;
  pointer-events: none !important;
  text-shadow: 2px 2px 20px rgba(255, 255, 255, 0.8) !important;
`;

const BackgroundReadyTitle = styled(Typography)`
  position: absolute !important;
  top: 1px !important;
  left: 30px !important;
  font-weight: 700 !important;
  font-size: 2rem !important;
  color: rgba(33, 150, 243, 0.6) !important;
  z-index: 1 !important;
  pointer-events: none !important;
  text-shadow: 2px 2px 20px rgba(255, 255, 255, 0.8) !important;
`;

const CompletedSection = styled(SectionContainer)`
  background-color: rgba(255, 152, 0, 0.15);
  border: 2px solid rgba(255, 152, 0, 0.3);
  margin-top: 10px;
  position: relative;
`;

const BackgroundCompletedTitle = styled(Typography)`
  position: absolute !important;
  top: 1px !important;
  left: 30px !important;
  font-weight: 700 !important;
  font-size: 2rem !important;
  color: rgba(255, 152, 0, 0.6) !important;
  z-index: 1 !important;
  pointer-events: none !important;
  text-shadow: 2px 2px 20px rgba(255, 255, 255, 0.8) !important;
`;

const OrderBoxStyled = styled(Card)`
  height: 100%;
  min-height: ${(props) =>
    props.$showItems ? DISPLAY_SETTINGS.boxStyling.minHeight : "150px"};
  max-width: ${DISPLAY_SETTINGS.boxStyling.maxWidth};
  transition: ${DISPLAY_SETTINGS.boxStyling.transition};
  animation: ${slideIn} 0.3s ease-out;
  display: flex;
  flex-direction: column;
  position: relative;

  &:hover {
    transform: ${DISPLAY_SETTINGS.boxStyling.hoverTransform};
  }

  &.moving-to-ready {
    animation: ${moveToReady} 0.8s ease-in-out forwards;
  }

  &.moving-to-in-progress {
    animation: ${moveToInProgress} 0.8s ease-in-out forwards;
  }

  &.moving-from-ready {
    animation: ${moveFromReady} 0.8s ease-in-out forwards;
  }

  &.moving-from-in-progress {
    animation: ${moveFromInProgress} 0.8s ease-in-out forwards;
  }

  &.removing {
    animation: ${slideOut} 0.3s ease-in forwards;
  }
`;

const OrderNumber = styled(Typography)`
  font-weight: ${STYLED_COMPONENTS.orderNumber.fontWeight} !important;
  font-size: ${STYLED_COMPONENTS.orderNumber.fontSize} !important;
  margin: ${STYLED_COMPONENTS.orderNumber.margin} !important;
  background-color: ${STYLED_COMPONENTS.orderNumber.backgroundColor} !important;
  color: ${STYLED_COMPONENTS.orderNumber.color} !important;
  padding: ${STYLED_COMPONENTS.orderNumber.padding} !important;
  border-radius: ${STYLED_COMPONENTS.orderNumber.borderRadius} !important;
  display: ${STYLED_COMPONENTS.orderNumber.display} !important;
  align-items: ${STYLED_COMPONENTS.orderNumber.alignItems} !important;
  justify-content: ${STYLED_COMPONENTS.orderNumber.justifyContent} !important;
  height: ${STYLED_COMPONENTS.orderNumber.height} !important;
  min-width: ${STYLED_COMPONENTS.orderNumber.minWidth} !important;
`;

const OrderItem = styled.div`
  display: ${STYLED_COMPONENTS.orderItem.display};
  justify-content: ${STYLED_COMPONENTS.orderItem.justifyContent};
  align-items: ${STYLED_COMPONENTS.orderItem.alignItems};
  padding: ${STYLED_COMPONENTS.orderItem.padding};
`;

const ItemName = styled(Typography)`
  font-weight: ${STYLED_COMPONENTS.itemName.fontWeight};
  font-size: ${STYLED_COMPONENTS.itemName.fontSize} !important;
  max-width: ${STYLED_COMPONENTS.itemName.maxWidth};
`;

const ItemQuantity = styled(Chip)`
  font-weight: ${STYLED_COMPONENTS.itemQuantity.fontWeight};
`;

// Reusable OrderBox Component
const OrderBox = ({
  order,
  animatingOrders,
  orderMovements,
  handleOrderClick,
  renderOrderContent,
  formatTimeDifference,
  showOrderItems,
  t,
  isCompleted = false,
  bulkMode = false,
  isSelected = false,
  onToggleSelect,
}) => {
  const movement = orderMovements.get(order.id);
  const isAnimating = animatingOrders.has(order.id);

  return (
    <Grid
      item
      xs={GRID_CONFIG.item.xs}
      sm={GRID_CONFIG.item.sm}
      md={GRID_CONFIG.item.md}
      lg={GRID_CONFIG.item.lg}
      xl={GRID_CONFIG.item.xl}
      key={order.id}
    >
      <OrderBoxStyled
        $showItems={showOrderItems}
        className={
          isAnimating
            ? (() => {
                if (movement) {
                  if (
                    movement.from === "inProgress" &&
                    movement.to === "ready"
                  ) {
                    return "moving-to-ready";
                  } else if (
                    movement.from === "ready" &&
                    movement.to === "inProgress"
                  ) {
                    return "moving-from-ready";
                  }
                }
                return "removing";
              })()
            : ""
        }
        sx={{
          backgroundColor: order.color,
          color: "white",
          cursor: "pointer",
          position: "relative",
          outline:
            bulkMode && isSelected ? "4px solid #1565c0" : "none",
          outlineOffset: "-2px",
          opacity: bulkMode && !isSelected ? 0.7 : 1,
          // minWidth: showOrderItems ? "250px" : "150px",
        }}
        onClick={() =>
          bulkMode ? onToggleSelect?.(order) : handleOrderClick(order)
        }
      >
        {bulkMode && isSelected ? (
          <Box
            sx={{
              position: "absolute",
              top: 6,
              right: 6,
              width: 26,
              height: 26,
              borderRadius: "50%",
              backgroundColor: "#1565c0",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 16,
              fontWeight: 700,
              zIndex: 2,
            }}
          >
            ✓
          </Box>
        ) : null}
        <CardContent
          sx={{
            p: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          <Box sx={STYLED_COMPONENTS.orderBoxContainer}>
            <Box sx={STYLED_COMPONENTS.orderInfoContainer}>
              <OrderNumber variant="h6">{order.orderNumber}</OrderNumber>
              {DISPLAY_SETTINGS.showLocator && order?.locator && (
                <Chip
                  label={`# ${order?.locator}`}
                  size="small"
                  sx={STYLED_COMPONENTS.locatorChip}
                />
              )}
              {order.takeaway && (
                <Chip
                  icon={<TakeoutDiningIcon />}
                  size="small"
                  sx={STYLED_COMPONENTS.takeawayChip}
                />
              )}
            </Box>
          </Box>

          {order.createdAt && (
            <Typography variant="caption">
              {format(new Date(order.createdAt), "dd.MM.yyyy HH:mm:ss", {
                locale: sk,
              })}
            </Typography>
          )}

          <Divider sx={STYLED_COMPONENTS.divider} />

          <Box sx={{ flex: 1 }}>{renderOrderContent(order)}</Box>
        </CardContent>
        {order.createdAt && !isCompleted && (
          <Typography variant="caption" sx={STYLED_COMPONENTS.timeAgo}>
            {formatTimeDifference(order.createdAt)}{" "}
          </Typography>
        )}
      </OrderBoxStyled>
    </Grid>
  );
};

const DSOKDispensingOrders = () => {
  const { t, i18n } = useTranslation();

  const { getAuthUser } = useJumboAuth();
  // const { customer } = getAuthUser() || {};
  const { customerCode, locationCode, env, customerId } = useParams();
  // const customerCode = customer?.dsokSettings?.customer?.code;
  const { socket, connect, subscribe, unsubscribe } = useSocket();
  const [inProgressOrders, setInProgressOrders] = useState([]);
  const [readyOrders, setReadyOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [showCompletedOrders, setShowCompletedOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isOrderDetailModalOpen, setIsOrderDetailModalOpen] = useState(false);
  const [animatingOrders, setAnimatingOrders] = useState(new Set());
  const [orderMovements, setOrderMovements] = useState(new Map()); // tracks source -> destination
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadOrders, setLoadOrders] = useState(true);
  const [isLocalStatusChange, setIsLocalStatusChange] = useState(false);
  const [showOrderItems, setShowOrderItems] = useState(() => {
    const saved = sessionStorage.getItem("dsokDispensingOrders_showItems");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [currentDateTime, setCurrentDateTime] = useState(new Date());
  const [showColons, setShowColons] = useState(true);
  // Hromadná zmena stavu: režim výberu + množina vybraných ID + prebiehajúca akcia
  const [bulkMode, setBulkMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkApplying, setBulkApplying] = useState(false);
  // Potvrdenie pri hromadnom zrušení — poistka proti omylu (zrušenie je koncové).
  const [bulkCancelConfirmOpen, setBulkCancelConfirmOpen] = useState(false);
  // Aktívny tab v detaile objednávky: "detail" | "history".
  const [detailTab, setDetailTab] = useState("detail");

  // Helper function to get consistent color for an order based on its ID
  const getOrderColor = (orderId) => {
    // Use the order ID to generate a consistent index
    const hash = orderId
      .toString()
      .split("")
      .reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0);
    const index = Math.abs(hash) % ORDER_BOX_COLORS.colors.length;
    return ORDER_BOX_COLORS.colors[index];
  };

  // Helper function to format time difference
  const formatTimeDifference = (orderDate) => {
    const orderTime = new Date(orderDate);
    const now = currentDateTime;

    const diffInSeconds = differenceInSeconds(now, orderTime);
    const diffInMinutes = differenceInMinutes(now, orderTime);
    const diffInHours = differenceInHours(now, orderTime);

    let result;
    if (diffInHours > 0) {
      result = `${diffInHours}h ${diffInMinutes % 60}m`;
    } else if (diffInMinutes > 0) {
      result = `${diffInMinutes}m ${diffInSeconds % 60}s`;
    } else {
      result = `${diffInSeconds}s`;
    }

    return result;
  };

  // Helper function to render order items or total count
  const renderOrderContent = (order) => {
    if (showOrderItems) {
      return (
        <>
          {order.items
            .slice(0, DISPLAY_SETTINGS.maxItemsPerBox)
            .map((item, index) => (
              <OrderItem key={index}>
                <ItemName variant="body2" sx={{ color: "white" }}>
                  {item.name}
                </ItemName>
                <ItemQuantity
                  label={item.quantity}
                  size="small"
                  sx={{
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "white",
                    fontWeight: 600,
                  }}
                />
              </OrderItem>
            ))}

          {DISPLAY_SETTINGS.showMoreItemsIndicator &&
            order.items.length > DISPLAY_SETTINGS.maxItemsPerBox && (
              <OrderItem>
                <ItemName
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    fontStyle: "italic",
                  }}
                ></ItemName>
              </OrderItem>
            )}

          {DISPLAY_SETTINGS.showTotalItems && (
            <OrderItem>
              <ItemName
                variant="body2"
                sx={{ color: "white", fontWeight: "bold" }}
              >
                {t("pages.dsokOrders.dispensingOrders.totalItems")}:{" "}
                {order.totalItems}
              </ItemName>
            </OrderItem>
          )}
        </>
      );
    } else {
      return null;
    }
  };

  // Save toggle state to session storage whenever it changes
  useEffect(() => {
    sessionStorage.setItem(
      "dsokDispensingOrders_showItems",
      JSON.stringify(showOrderItems)
    );
  }, [showOrderItems]);

  // Update date and time every second and blink colons
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentDateTime(new Date());
      setShowColons((prev) => !prev);
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // WebSocket connection
  useEffect(() => {
    if (connect) {
      const baseURL = window.location.origin;
      // const baseURL = getBEUrl().replace(/\/$/, ""); // Remove trailing slash
      const wsURL = baseURL
        .replace(/^https:\/\//, "wss://")
        .replace(/^http:\/\//, "ws://"); // Convert to appropriate WebSocket protocol

      // jwtAuthKioskAxios.defaults.baseURL = `${wsURL}/`;
      connect(wsURL);
    }
  }, [connect]);

  // WebSocket subscription for order preparation status changes
  useEffect(() => {
    if (socket?.active && customerCode) {
      const channel = `admin/dsokOrderPreparationStatus/${customerCode}`;
      const handleUpdate = (message) => {
        if (message === "refresh" && !isLocalStatusChange) {
          setLoadOrders(true);
        } else if (message === "refresh" && isLocalStatusChange) {
        }
      };

      subscribe(channel, handleUpdate);

      return () => {
        unsubscribe(channel, handleUpdate);
      };
    }
  }, [
    socket?.active,
    customerCode,
    subscribe,
    unsubscribe,
    isLocalStatusChange,
  ]);

  // Fallback polling — periodicky aktualizuje obsah (bez reloadu stránky), keby WS realtime
  // vypadol (socket.io transport býva nespoľahlivý). Nerefetchuje počas lokálnej zmeny stavu,
  // aby neprepísal práve prebiehajúcu operátorovu akciu.
  useEffect(() => {
    const POLL_MS = 10000;
    const timer = setInterval(() => {
      if (!isLocalStatusChange) {
        setLoadOrders(true);
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [isLocalStatusChange]);

  // Load orders data
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        // setLoading(true);
        setError(null);

        // Fetch orders from API
        const ordersData = await getDSOKOrdersForStatusScreen(
          locationCode,
          env
        );

        // Add colors to each order based on order ID for consistency
        const inProgressWithColors = (ordersData.inProgressOrders || []).map(
          (order) => {
            return {
              ...order,
              color: getOrderColor(order.id),
            };
          }
        );

        const readyWithColors = (ordersData.readyOrders || []).map((order) => {
          return {
            ...order,
            color: getOrderColor(order.id),
          };
        });

        setInProgressOrders(inProgressWithColors);
        setReadyOrders(readyWithColors);
        setLoadOrders(false); // Reset loadOrders after successful fetch
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError(
          err.message ||
            t("pages.dsokOrders.dispensingOrders.failedToFetchOrders")
        );

        setLoadOrders(false); // Reset loadOrders even on error
      } finally {
        setLoading(false);
      }
    };

    if (customerCode && !isLocalStatusChange) {
      fetchOrders();
    } else if (customerCode && isLocalStatusChange) {
    }
  }, [customerCode, loadOrders, isLocalStatusChange]);

  // Modal handlers
  const handleOrderClick = (order) => {
    setSelectedOrder(order);

    // If order is from completed orders, open order detail modal directly
    if (
      showCompletedOrders &&
      completedOrders.some((completedOrder) => completedOrder.id === order.id)
    ) {
      setDetailTab("detail");
      setIsOrderDetailModalOpen(true);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  const handleCancelOrder = () => {
    setIsModalOpen(false);
    setIsCancelDialogOpen(true);
  };

  const handleCancelDialogClose = () => {
    setIsCancelDialogOpen(false);
    setSelectedOrder(null);
  };

  const handleOrderDetailModalClose = () => {
    setIsOrderDetailModalOpen(false);
  };

  const handleOrderDetail = () => {
    setDetailTab("detail");
    setIsOrderDetailModalOpen(true);
  };

  // Znormalizovaná história zmien stavu prípravy pre detail objednávky.
  const renderStatusHistory = () => {
    const legacy = Array.isArray(selectedOrder?.preparationStatusHistory)
      ? selectedOrder.preparationStatusHistory
      : [];
    const items = legacy.length
      ? legacy.map((h) => ({
          status: h.status,
          at: h.timestamp,
          changedBy: h.changedBy,
          cancelReason: h.cancelReason,
        }))
      : (Array.isArray(selectedOrder?.statusHistory)
          ? selectedOrder.statusHistory
          : []
        )
          .filter((h) => h.type === "preparation")
          .map((h) => ({
            status: h.status,
            at: h.at,
            changedBy: h.changedBy,
            cancelReason: h.cancelReason,
          }));

    if (!items.length) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("pages.dsokOrders.dispensingOrders.noHistory", {
            defaultValue: "Zatiaľ žiadne zmeny stavu.",
          })}
        </Typography>
      );
    }

    const fmt = (at) => {
      if (!at) return "";
      const d = new Date(String(at).replace(" ", "T"));
      return isNaN(d.getTime())
        ? String(at)
        : format(d, "dd.MM.yyyy HH:mm", { locale: sk });
    };

    return (
      <Stack gap={1} sx={{ mt: 1, mb: 2 }}>
        {items.map((h, i) => {
          const c = preparationStatusColor(h.status);
          return (
            <Stack
              key={i}
              direction="row"
              gap={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <Box
                component="span"
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: 12,
                  fontWeight: 600,
                  bgcolor: c.bg,
                  color: c.color,
                  minWidth: 104,
                  textAlign: "center",
                }}
              >
                {t(preparationStatusLabelKey(h.status), h.status)}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {fmt(h.at)}
              </Typography>
              {h.changedBy ? (
                <Typography variant="caption" color="text.secondary">
                  · {h.changedBy}
                </Typography>
              ) : null}
              {h.cancelReason?.code ? (
                <Typography variant="caption" color="text.secondary">
                  · {h.cancelReason.code}
                </Typography>
              ) : null}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder) return;

    // Set flag to prevent WebSocket refresh during local status change
    setIsLocalStatusChange(true);

    let canMove = false;
    try {
      // Map UI status to backend status format
      const backendStatus =
        newStatus === "inProgress" ? "inProgress" : newStatus;

      // Call backend API to update order preparation status
      const result = await updateOrderPreparationStatus(
        selectedOrder.id,
        backendStatus,
        customerCode,
        env,
        customerId
      );
      if (result?.status === "success") {
        canMove = true;
      }
    } catch (error) {
      console.error("Error updating order status in backend:", error);
      // Continue with UI update even if backend call fails
    }

    if (!canMove) {
      setIsLocalStatusChange(false);
      return;
    }

    // Track the movement
    const movement = {
      from: selectedOrder.status,
      to: newStatus,
      orderId: selectedOrder.id,
    };
    setOrderMovements((prev) => new Map(prev.set(selectedOrder.id, movement)));

    setAnimatingOrders((prev) => new Set([...prev, selectedOrder.id]));

    // Update the order status
    const updatedOrder = { ...selectedOrder, status: newStatus };

    // Phase 1: Animate exit from current section
    setTimeout(() => {
      if (newStatus === "ready") {
        // Remove from in progress
        setInProgressOrders((prev) =>
          prev.filter((order) => order.id !== selectedOrder.id)
        );
      } else if (newStatus === "inProgress") {
        // Remove from ready
        setReadyOrders((prev) =>
          prev.filter((order) => order.id !== selectedOrder.id)
        );
      }

      // Phase 2: Add to new section with entry animation
      setTimeout(() => {
        if (newStatus === "ready") {
          setReadyOrders((prev) => [updatedOrder, ...prev]);
        } else if (newStatus === "inProgress") {
          setInProgressOrders((prev) => [updatedOrder, ...prev]);
        }

        // Phase 3: Clean up animations and reset local status change flag
        setTimeout(() => {
          setAnimatingOrders((prev) => {
            const newSet = new Set(prev);
            newSet.delete(selectedOrder.id);
            return newSet;
          });
          setOrderMovements((prev) => {
            const newMap = new Map(prev);
            newMap.delete(selectedOrder.id);
            return newMap;
          });
          setIsLocalStatusChange(false);
          if (newStatus === "canceled") {
            setIsCancelDialogOpen(false);
            setSelectedOrder(null);
          }
        }, 200);
      }, 100);
    }, 400); // Exit animation duration

    setIsModalOpen(false);
    setSelectedOrder(null);
  };

  // --- Hromadná zmena stavu ---
  const toggleBulkMode = () => {
    setBulkMode((prev) => !prev);
    setSelectedIds(new Set());
  };

  const toggleSelectOrder = (order) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(order.id)) next.delete(order.id);
      else next.add(order.id);
      return next;
    });
  };

  // Vybrať/odznačiť všetky (len akčné stĺpce: pripravuje sa + pripravené).
  const selectAllBulk = () => {
    const all = [...inProgressOrders, ...readyOrders].map((o) => o.id);
    const allSelected =
      all.length > 0 && all.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(all));
  };

  const applyBulkStatus = async (newStatus) => {
    if (bulkApplying || selectedIds.size === 0) return;
    setBulkApplying(true);
    try {
      await updateOrderPreparationStatusBatch(
        Array.from(selectedIds),
        newStatus,
        customerCode,
        env,
        customerId,
      );
    } catch (error) {
      console.error("Bulk status update failed:", error);
    } finally {
      setSelectedIds(new Set());
      setBulkMode(false);
      setBulkApplying(false);
      // Refetch zo servera (namiesto lokálnej animácie) — jednoduché a spoľahlivé.
      setIsLocalStatusChange(false);
      setLoadOrders(true);
    }
  };

  if (loading) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              {t("pages.dsokOrders.dispensingOrders.loadingOrders")}
            </Typography>
            <CircularProgress />
          </Box>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ py: 3, textAlign: "center" }}>
            <Typography variant="h6" color="error" sx={{ mb: 2 }}>
              {t("pages.dsokOrders.dispensingOrders.errorLoadingOrders")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {error}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {/* Hide the top sticky toolbar on the base route (/customer/dsok/dispensing-orders). */}
        {customerCode && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              p: 1,
              borderRadius: 1,
              position: "sticky",
              top: 0,
              zIndex: 10,
              backgroundColor: "background.paper",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={showOrderItems}
                    onChange={(e) => setShowOrderItems(e.target.checked)}
                    color="primary"
                  />
                }
                label={t("pages.dsokOrders.dispensingOrders.displayOrderItems")}
                sx={{ fontWeight: "bold" }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={bulkMode}
                    onChange={toggleBulkMode}
                    color="secondary"
                  />
                }
                label={t("pages.dsokOrders.dispensingOrders.bulkMode", {
                  defaultValue: "Hromadná zmena",
                })}
                sx={{ fontWeight: "bold" }}
              />
              {bulkMode ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={selectAllBulk}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {t("pages.dsokOrders.dispensingOrders.bulkSelectAll", {
                    defaultValue: "Vybrať všetky",
                  })}
                </Button>
              ) : null}
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: "bold",
                color: "text.primary",
                textAlign: "center",
                fontSize: "1.2rem",
              }}
            >
              {format(currentDateTime, "dd.MM.yyyy", { locale: sk })}{" "}
              {format(currentDateTime, "HH", { locale: sk })}
              <span
                style={{
                  opacity: showColons ? 1 : 0,
                  transition: "opacity 0.1s",
                }}
              >
                :
              </span>
              {format(currentDateTime, "mm", { locale: sk })}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <IconButton
                onClick={() => window.location.reload()}
                sx={{
                  backgroundColor: "#E91E63",
                  color: "white",
                  padding: "8px",
                  "&:hover": {
                    backgroundColor: "#C2185B",
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
              <Button
                variant="contained"
                size="medium"
                onClick={async () => {
                  if (showCompletedOrders) {
                    // Switch back to in-progress and ready orders
                    setShowCompletedOrders(false);
                    // Fetch in-progress and ready orders
                    try {
                      const ordersData = await getDSOKOrdersForStatusScreen(
                        locationCode,
                        env,
                        "all"
                      );

                      const inProgressWithColors = (
                        ordersData.inProgressOrders || []
                      ).map((order) => ({
                        ...order,
                        color: getOrderColor(order.id),
                      }));

                      const readyWithColors = (ordersData.readyOrders || []).map(
                        (order) => ({
                          ...order,
                          color: getOrderColor(order.id),
                        })
                      );

                      setInProgressOrders(inProgressWithColors);
                      setReadyOrders(readyWithColors);
                    } catch (error) {
                      console.error(
                        "Error fetching in-progress and ready orders:",
                        error
                      );
                    }
                  } else {
                    // Complete all ready orders
                    for (const order of readyOrders) {
                      await handleStatusChange("completed", order.id);
                    }

                    // Fetch completed orders from backend
                    try {
                      const completedOrdersData =
                        await getDSOKOrdersForStatusScreen(
                          locationCode,
                          env,
                          "completed"
                        );
                      console.log(
                        "Completed orders fetched:",
                        completedOrdersData
                      );

                      // If completed orders exist, show them and hide in-progress/ready
                      if (
                        completedOrdersData &&
                        completedOrdersData.completedOrders?.length > 0
                      ) {
                        const completedWithColors = [
                          ...(completedOrdersData.completedOrders || []),
                        ].map((order) => ({
                          ...order,
                          color: getOrderColor(order.id),
                        }));

                        setCompletedOrders(completedWithColors);
                        setShowCompletedOrders(true);
                        setInProgressOrders([]);
                        setReadyOrders([]);
                      }
                    } catch (error) {
                      console.error("Error fetching completed orders:", error);
                    }
                  }
                }}
                sx={{
                  backgroundColor: "#FF9800",
                  color: "white",
                  fontWeight: "600",
                  textTransform: "none",
                  padding: "8px 16px",
                  fontSize: "0.9rem",
                  "&:hover": {
                    backgroundColor: "#F57C00",
                  },
                }}
              >
                {showCompletedOrders
                  ? t("pages.dsokOrders.dispensingOrders.inProgressOrders")
                  : t("pages.dsokOrders.dispensingOrders.completeOrders")}
              </Button>
              <IconButton
                onClick={() => i18n.changeLanguage("sk")}
                sx={{
                  p: 0.5,
                  border:
                    i18n.language === "sk"
                      ? "2px solid #1976d2"
                      : "2px solid transparent",
                  borderRadius: "4px",
                }}
              >
                <img
                  src="/images/flag/sk_flag.jpg"
                  style={{
                    width: "32px",
                    height: "24px",
                    objectFit: "cover",
                    borderRadius: "2px",
                  }}
                  alt={t("pages.dsokOrders.dispensingOrders.slovak")}
                />
              </IconButton>

              <IconButton
                onClick={() => i18n.changeLanguage("cs")}
                sx={{
                  p: 0.5,
                  border:
                    i18n.language === "cs"
                      ? "2px solid #1976d2"
                      : "2px solid transparent",
                  borderRadius: "4px",
                }}
              >
                <img
                  src="/images/flag/cz_flag.jpg"
                  style={{
                    width: "32px",
                    height: "24px",
                    objectFit: "cover",
                    borderRadius: "2px",
                  }}
                  alt={t("pages.dsokOrders.dispensingOrders.czech")}
                />
              </IconButton>

              <IconButton
                onClick={() => i18n.changeLanguage("en")}
                sx={{
                  p: 0.5,
                  border:
                    i18n.language === "en"
                      ? "2px solid #1976d2"
                      : "2px solid transparent",
                  borderRadius: "4px",
                }}
              >
                <img
                  src="/images/flag/en_flag.jpg"
                  style={{
                    width: "32px",
                    height: "24px",
                    objectFit: "cover",
                    borderRadius: "2px",
                  }}
                  alt={t("pages.dsokOrders.dispensingOrders.english")}
                />
              </IconButton>

              <IconButton
                onClick={() => i18n.changeLanguage("de")}
                sx={{
                  p: 0.5,
                  border:
                    i18n.language === "de"
                      ? "2px solid #1976d2"
                      : "2px solid transparent",
                  borderRadius: "4px",
                }}
              >
                <img
                  src="/images/flag/de_flag.jpg"
                  style={{
                    width: "32px",
                    height: "24px",
                    objectFit: "cover",
                    borderRadius: "2px",
                  }}
                  alt={t("pages.dsokOrders.dispensingOrders.german")}
                />
              </IconButton>
            </Box>
          </Box>
        )}

        <Box>
          {showCompletedOrders ? (
            /* Completed Orders Section */
            <CompletedSection>
              <BackgroundCompletedTitle variant="h3" component="h2">
                {t("pages.dsokOrders.dispensingOrders.completed")} -{" "}
                {t("pages.dsokOrders.dispensingOrders.completedInfo")}
              </BackgroundCompletedTitle>
              <Grid
                container
                spacing={0.625}
                alignItems="center"
                sx={{ mt: 1.25 }}
              >
                {completedOrders.map((order) => (
                  <OrderBox
                    key={order.id}
                    order={order}
                    animatingOrders={animatingOrders}
                    orderMovements={orderMovements}
                    handleOrderClick={handleOrderClick}
                    renderOrderContent={renderOrderContent}
                    formatTimeDifference={formatTimeDifference}
                    showOrderItems={showOrderItems}
                    t={t}
                    isCompleted={true}
                  />
                ))}
              </Grid>
            </CompletedSection>
          ) : (
            <>
              {/* In Progress Section */}
              <InProgressSection>
                <BackgroundInProgressTitle variant="h3" component="h2">
                  {t("pages.dsokOrders.dispensingOrders.inProgress")}
                </BackgroundInProgressTitle>
                <Grid
                  container
                  spacing={0.625}
                  alignItems="center"
                  sx={{ mt: 1.25 }}
                >
                  {inProgressOrders.map((order) => (
                    <OrderBox
                      key={order.id}
                      order={order}
                      animatingOrders={animatingOrders}
                      orderMovements={orderMovements}
                      handleOrderClick={handleOrderClick}
                      renderOrderContent={renderOrderContent}
                      formatTimeDifference={formatTimeDifference}
                      showOrderItems={showOrderItems}
                      t={t}
                      bulkMode={bulkMode}
                      isSelected={selectedIds.has(order.id)}
                      onToggleSelect={toggleSelectOrder}
                    />
                  ))}
                </Grid>
              </InProgressSection>

              {/* Ready Section */}
              <ReadySection>
                <BackgroundReadyTitle variant="h3" component="h2">
                  {t("pages.dsokOrders.dispensingOrders.ready")}
                </BackgroundReadyTitle>
                <Grid
                  container
                  spacing={0.625}
                  alignItems="center"
                  sx={{ mt: 1.25 }}
                >
                  {readyOrders.map((order) => (
                    <OrderBox
                      key={order.id}
                      order={order}
                      animatingOrders={animatingOrders}
                      orderMovements={orderMovements}
                      handleOrderClick={handleOrderClick}
                      renderOrderContent={renderOrderContent}
                      formatTimeDifference={formatTimeDifference}
                      showOrderItems={showOrderItems}
                      t={t}
                      bulkMode={bulkMode}
                      isSelected={selectedIds.has(order.id)}
                      onToggleSelect={toggleSelectOrder}
                    />
                  ))}
                </Grid>
              </ReadySection>
            </>
          )}
        </Box>
      </Grid>

      {/* Hromadná zmena stavu — plávajúci akčný panel */}
      {bulkMode && selectedIds.size > 0 ? (
        <Box
          sx={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
            bgcolor: "background.paper",
            boxShadow: 6,
            borderRadius: 3,
            px: 2.5,
            py: 1.5,
            maxWidth: "95vw",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mr: 1 }}>
            {t("pages.dsokOrders.dispensingOrders.bulkSelected", {
              defaultValue: "Vybraných: {{count}}",
              count: selectedIds.size,
            })}
          </Typography>
          {[
            PREPARATION_STATUSES.READY,
            PREPARATION_STATUSES.COMPLETED,
            PREPARATION_STATUSES.CANCELED,
          ].map((s) => {
            const c = preparationStatusColor(s);
            return (
              <Button
                key={s}
                variant="contained"
                disableElevation
                disabled={bulkApplying}
                onClick={() =>
                  s === PREPARATION_STATUSES.CANCELED
                    ? setBulkCancelConfirmOpen(true)
                    : applyBulkStatus(s)
                }
                sx={{
                  bgcolor: c.bg,
                  color: c.color,
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": { bgcolor: c.bg, filter: "brightness(0.95)" },
                }}
              >
                {t(preparationStatusLabelKey(s), s)}
              </Button>
            );
          })}
          {bulkApplying ? (
            <CircularProgress size={22} />
          ) : (
            <Button
              variant="text"
              onClick={() => setSelectedIds(new Set())}
              sx={{ textTransform: "none" }}
            >
              {t("pages.dsokOrders.dispensingOrders.bulkClear", {
                defaultValue: "Zrušiť výber",
              })}
            </Button>
          )}
        </Box>
      ) : null}

      {/* Potvrdenie hromadného zrušenia */}
      <Dialog
        open={bulkCancelConfirmOpen}
        onClose={() => setBulkCancelConfirmOpen(false)}
      >
        <DialogTitle>
          {t("pages.dsokOrders.dispensingOrders.bulkCancelTitle", {
            defaultValue: "Zrušiť vybrané objednávky?",
          })}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("pages.dsokOrders.dispensingOrders.bulkCancelConfirm", {
              defaultValue:
                "Naozaj chcete zrušiť {{count}} objednávok? Túto akciu nie je možné vrátiť.",
              count: selectedIds.size,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkCancelConfirmOpen(false)}
            disabled={bulkApplying}
          >
            {t("buttons.no", { defaultValue: "Nie" })}
          </Button>
          <Button
            color="error"
            variant="contained"
            disableElevation
            disabled={bulkApplying}
            onClick={() => {
              setBulkCancelConfirmOpen(false);
              applyBulkStatus(PREPARATION_STATUSES.CANCELED);
            }}
          >
            {t("pages.dsokOrders.dispensingOrders.bulkCancelYes", {
              defaultValue: "Áno, zrušiť",
            })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Action Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="order-action-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={MODAL_CONFIG.modalContent}>
          {/* Order Detail Button */}
          <Button
            variant="outlined"
            size="small"
            onClick={handleOrderDetail}
            sx={{
              position: "absolute",
              right: 50,
              top: 8,
              color: "primary.main",
              borderColor: "primary.main",
              fontSize: "1.3rem",
              fontWeight: "bold",
              textTransform: "none",
            }}
          >
            {t("pages.dsokOrders.dispensingOrders.orderDetail")}
          </Button>

          {/* Close Button */}
          <IconButton
            onClick={handleCloseModal}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Modal Content */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h2" gutterBottom>
              {t("pages.dsokOrders.orderCode")} #{selectedOrder?.orderNumber}
            </Typography>

            {selectedOrder?.locator && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("pages.dsokOrders.dispensingOrders.locator")}: #
                {selectedOrder.locator}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary" gutterBottom>
              {t("pages.dsokOrders.orderStatus")}:{" "}
              {selectedOrder?.status === "inProgress"
                ? t("pages.dsokOrders.dispensingOrders.inProgress")
                : t("pages.dsokOrders.dispensingOrders.ready")}
            </Typography>
          </Box>

          <Box sx={MODAL_CONFIG.modalButtonsContainer}>
            <Button
              variant="contained"
              size="large"
              sx={{ ...MODAL_CONFIG.modalButton, ...MODAL_CONFIG.cancelButton }}
              onClick={handleCancelOrder}
            >
              {t("pages.dsokOrders.dispensingOrders.cancelOrder")}
            </Button>

            {selectedOrder?.status !== "ready" && (
              <Button
                variant="contained"
                size="large"
                sx={{
                  ...MODAL_CONFIG.modalButton,
                  ...MODAL_CONFIG.readyButton,
                }}
                onClick={() => handleStatusChange("ready")}
              >
                {t("pages.dsokOrders.dispensingOrders.ready")}
              </Button>
            )}

            {selectedOrder?.status !== "inProgress" && (
              <Button
                variant="contained"
                size="large"
                sx={{
                  ...MODAL_CONFIG.modalButton,
                  ...MODAL_CONFIG.inProgressButton,
                }}
                onClick={() => handleStatusChange("inProgress")}
              >
                {t("pages.dsokOrders.dispensingOrders.inProgress")}
              </Button>
            )}

            {selectedOrder?.status === "ready" && (
              <Button
                variant="contained"
                size="large"
                sx={{
                  ...MODAL_CONFIG.modalButton,
                  ...MODAL_CONFIG.completeButton,
                }}
                onClick={() => handleStatusChange("completed")}
              >
                {t("pages.dsokOrders.dispensingOrders.complete")}
              </Button>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={isCancelDialogOpen}
        onClose={handleCancelDialogClose}
        aria-labelledby="cancel-dialog-title"
      >
        <DialogTitle id="cancel-dialog-title">
          {t("pages.dsokOrders.dispensingOrders.confirmOrderCancellation")}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t("pages.dsokOrders.dispensingOrders.confirmCancelMessage", {
              orderNumber: selectedOrder?.orderNumber,
            })}
            <br />
            {t("pages.dsokOrders.dispensingOrders.actionCannotBeUndone")}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCancelDialogClose}
            color="primary"
            sx={{ padding: "50px" }}
          >
            {t("pages.dsokOrders.dispensingOrders.noKeepOrder")}
          </Button>
          <Button
            onClick={() => handleStatusChange("canceled")}
            color="error"
            variant="contained"
            sx={{ padding: "50px" }}
          >
            {t("pages.dsokOrders.dispensingOrders.yesCancelOrder")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Order Detail Modal */}
      <Modal
        open={isOrderDetailModalOpen}
        onClose={handleOrderDetailModalClose}
        aria-labelledby="order-detail-modal"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: 800,
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          {/* Close Button */}
          <IconButton
            onClick={handleOrderDetailModalClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Modal Content */}
          <Box sx={{ flex: 1, mt: 2 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {t("pages.dsokOrders.dispensingOrders.orderDetails")} - #
              {selectedOrder?.orderNumber}
            </Typography>

            <Tabs
              value={detailTab}
              onChange={(_, v) => setDetailTab(v)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              <Tab
                value="detail"
                label={t("pages.dsokOrders.dispensingOrders.detailTab", {
                  defaultValue: "Detail",
                })}
              />
              <Tab
                value="history"
                label={t("pages.dsokOrders.dispensingOrders.historyTab", {
                  defaultValue: "História stavov",
                })}
              />
            </Tabs>

            {detailTab === "history" ? (
              renderStatusHistory()
            ) : (
              <>
                {/* Order Information */}
                <Box sx={{ mb: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("pages.dsokOrders.dispensingOrders.locator")}:{" "}
                    <strong>#{selectedOrder?.locator}</strong>
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t("pages.dsokOrders.dispensingOrders.created")}:{" "}
                    <strong>
                      {selectedOrder?.createdAt &&
                        format(
                          new Date(selectedOrder.createdAt),
                          "dd.MM.yyyy HH:mm:ss",
                          { locale: sk }
                        )}
                    </strong>
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Order Items */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t("pages.dsokOrders.dispensingOrders.orderItems")} (
                {selectedOrder?.totalItems || 0})
              </Typography>
              <Box sx={{ maxHeight: "300px", overflow: "auto" }}>
                {selectedOrder?.items?.map((item, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      p: 2,
                      mb: 1,
                      backgroundColor: "#fafafa",
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={6}>
                        <Typography variant="body1" fontWeight="bold">
                          {item.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="text.secondary">
                          {t("pages.dsokOrders.dispensingOrders.qty")}:{" "}
                          {item.quantity}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="text.secondary">
                          {t("pages.dsokOrders.dispensingOrders.price")}:{" "}
                          {item.itemPrice}{" "}
                          {selectedOrder?.currencySymbol || "€"}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="body2" color="text.secondary">
                          {t("pages.dsokOrders.dispensingOrders.total")}:{" "}
                          {item.totalPrice}{" "}
                          {selectedOrder?.currencySymbol || "€"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {t("pages.dsokOrders.dispensingOrders.vat")}:{" "}
                          {item.vatRate}% (
                          {t("pages.dsokOrders.dispensingOrders.base")}:{" "}
                          {item.vatBase},{" "}
                          {t("pages.dsokOrders.dispensingOrders.vat")}:{" "}
                          {item.vat})
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Order Totals */}
            {selectedOrder?.totals && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t("pages.dsokOrders.dispensingOrders.orderTotals")}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.dsokOrders.dispensingOrders.subtotal")}:{" "}
                      <strong>
                        {selectedOrder.totals.subtotal}{" "}
                        {selectedOrder?.currencySymbol || "€"}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.dsokOrders.dispensingOrders.vat")}:{" "}
                      <strong>
                        {selectedOrder.totals.vat}{" "}
                        {selectedOrder?.currencySymbol || "€"}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={4}>
                    <Typography variant="body2" color="text.secondary">
                      {t("pages.dsokOrders.dispensingOrders.total")}:{" "}
                      <strong>
                        {selectedOrder.totals.total}{" "}
                        {selectedOrder?.currencySymbol || "€"}
                      </strong>
                    </Typography>
                  </Grid>
                </Grid>

                {/* VAT Breakdown */}
                {selectedOrder.totals.vatBreakdown && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {t("pages.dsokOrders.dispensingOrders.vatBreakdown")}
                    </Typography>
                    {Object.entries(selectedOrder.totals.vatBreakdown).map(
                      ([key, vatInfo]) => (
                        <Typography
                          key={key}
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {vatInfo.rate}%{" "}
                          {t("pages.dsokOrders.dispensingOrders.vat")}:{" "}
                          {vatInfo.vat} {selectedOrder?.currencySymbol || "€"} (
                          {t("pages.dsokOrders.dispensingOrders.base")}:{" "}
                          {vatInfo.base} {selectedOrder?.currencySymbol || "€"},
                          {t("pages.dsokOrders.dispensingOrders.total")}:{" "}
                          {vatInfo.total} {selectedOrder?.currencySymbol || "€"}
                          )
                        </Typography>
                      )
                    )}
                  </Box>
                )}
              </Box>
            )}
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </Grid>
  );
};

export default DSOKDispensingOrders;
